import {React, useState, useEffect, useRef, createContext} from 'react';
import {Route, Routes, Link, useLocation } from 'react-router-dom';

import styles from './app.module.css'

import Home from './components/Home/Home.js';
import Header from './components/Home/Header/Header.js';
import AboutUs from './components/Home/AboutUs/AboutUs.js';
import ContactUs from './components/Home/ContactUs/ContactUs.js';
import Leagues from './components/Home/Leagues/Leagues.js'
import Registration from './components/Home/Registration/Registration.js';
import Privacy from './components/Home/Privacy/Privacy.js';
import Footer from './components/Home/Footer/Footer.js';


import constants from './constants.js'
import api from './routes/api.js'
import utils from './utils/utils.js';

import { useApiProvider } from './providers/ApiProvider.js';
import { useSidebarProvider } from './providers/SidebarProvider.js';
import { useLeagueTypeProvider } from './providers/LeagueTypeProvider';

import Sidebar from './components/main/Sidebar/Sidebar.js';
import MessageDialog from './components/common/MessageDialog/MessageDialog.js';

import dummy from './dummyData.js';
import IncidentReport from './components/Home/IncidentReport/IncidentReport.js';


const App = () => {

  const { setDialog, isLoading, setIsLoading, LoadingSpinner} = useApiProvider();
  const { isSidebarShowing, toggleSidebar } = useSidebarProvider();
  const { leagueType, setLeagueType } = useLeagueTypeProvider();

  const [selectedMenuItem, setSelectedMenuItem] = useState("");

  const [currentWeekMatchups, setCurrentWeekMatchups] = useState([]);
  const [lastWeekMatchups, setLastWeekMatchups] = useState([]);

  //To whoever comes after, good lord please don't drill these down. Just make them a damn context.
  // Thanks, love you.
  const [locationList, setLocationList] = useState()
  const [selectedLocation, setSelectedLocation] = useState({
    locationId: "",
    locationName: "",
  });
  const [selectedDivision, setSelectedDivision] = useState({
    divisionId: "",
    divisionName: "",
  });

  // Always in a 'm-xxxx' format. m=season xxxx=year
  const [selectedSeason, setSelectedSeason] = useState("");
  const [seasonList, setSeasonList] = useState([]);

  // These could probably be one method. sue me
  const getCurrentWeek = () => {
    const today = new Date();

    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0'); // months are 0-based
    const dd = String(today.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
  }
  
  const getLastWeek = () => {
    const today = new Date();
    today.setDate(today.getDate() - 7);

    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0'); // months are 0-based
    const dd = String(today.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
  }

  const NotFound = () => (
    <div className={styles.NotFound}>
      <h1>404 - Page Not Found</h1>
      <Link to="/"><div style={{fontSize: '1.4rem'}}>Go back home</div></Link>
      <Link to="/leagues/dashboard"><div style={{fontSize: '1.4rem'}}>Go to leagues dashboard</div></Link>
    </div>
  );

  // Reset the selected season when the leagueType changes
  useEffect(() => {
    setSelectedSeason("");
  }, [leagueType]);

  useEffect(() => {
    setIsLoading(true);
    const getSeason = async () => {
      try{
        // Get the users position. If you can't, default to NJ/PA. Coordinates are for Trenton NJ
        const position = await new Promise((resolve) =>
          navigator.geolocation.getCurrentPosition(resolve, () => resolve(null))
        );

        const baseUrl = utils.getBaseUrl(leagueType);
        var seasonResponse;
        var locationsResponse;
        
        if (selectedSeason == "") {
          seasonResponse = await api.fetch(baseUrl, constants.uri.seasons);

          if (leagueType === constants.pages.localLeagues) {
            locationsResponse = await api.fetch(baseUrl, constants.uri.clubs);
          }
          else {
            locationsResponse = await api.fetch(baseUrl, constants.uri.regions, (position ? `?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}` : "?latitude=40.2202&longitude=-74.7642"));
          }

          if (!seasonResponse.hasOwnProperty([constants.values])) throw seasonResponse;
          if (!locationsResponse.hasOwnProperty([constants.values])) throw locationsResponse;

          // These two are only for the home screen, which should always be up to date regardless of season selected
          const currentWeekMatchupsResponse = await api.fetch(baseUrl, constants.uri.listMatchups(getCurrentWeek()));
          const lastWeekMatchupsResponse = await api.fetch(baseUrl, constants.uri.listMatchups(getLastWeek()));

          if (!currentWeekMatchupsResponse.hasOwnProperty([constants.values])) throw currentWeekMatchupsResponse;
          if (!lastWeekMatchupsResponse.hasOwnProperty([constants.values])) throw lastWeekMatchupsResponse;

          // These methods prepare the matchups for the cards on the home page. Each card is a location/division with
          // all of the matchups for that week.
          const currentMatchups = Object.values(
            currentWeekMatchupsResponse[constants.values].reduce((acc, item) => {
              if (!acc[item.divisionId]) {
                acc[item.divisionId] = { [item.divisionId]: [] };
              }
              acc[item.divisionId][item.divisionId].push(item);
              return acc;
            }, {})
          );
          const previousMatchups = Object.values(
            lastWeekMatchupsResponse[constants.values].reduce((acc, item) => {
              if (!acc[item.divisionId]) {
                acc[item.divisionId] = { [item.divisionId]: [] };
              }
              acc[item.divisionId][item.divisionId].push(item);
              return acc;
            }, {})
          );
          // Only set here if the first time through
          setSelectedSeason(seasonResponse[constants.values][0][constants.api.seasonNumber] + "-" + seasonResponse[constants.values][0][constants.api.seasonYear]);
          setSeasonList(seasonResponse[constants.values]);
          setCurrentWeekMatchups(currentMatchups);
          setLastWeekMatchups(previousMatchups);
        }
        else {
          const seasonInfo = selectedSeason.split("-");
          if (leagueType === constants.pages.localLeagues) {
            locationsResponse = await api.fetch(baseUrl, constants.uri.clubs);
          }
          else {
            locationsResponse = await api.fetch(baseUrl, constants.uri.regions, 
              (position ? `?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}` : "?latitude=40.2202&longitude=-74.7642") + `&seasonNumber=${seasonInfo[0]}&seasonYear=${seasonInfo[1]}`);
          }
          if (!locationsResponse.hasOwnProperty([constants.values])) throw locationsResponse;
        }
        
        let list = [];
        locationsResponse[constants.values].forEach((location) => {

          let locationObj = {};
          let divisions = [];

          locationObj[constants.locationId] = leagueType===constants.pages.localLeagues ? location[constants.clubId] : location[constants.regionId];
          locationObj[constants.locationName] = leagueType===constants.pages.localLeagues ? location[constants.name] : location[constants.location];
          
          location[constants.divisions][constants.values].forEach((division) => {
            let divisionObj = {};
            divisionObj[constants.divisionId] = division[constants.divisionId];
            divisionObj[constants.divisionName] = division[constants.divisionName];
          
            divisions.push(divisionObj);
          });

          locationObj[constants.divisions] = divisions;

          list.push(locationObj);
        });

        const currentLocation = {};
        currentLocation[constants.locationId] = list[0][constants.locationId];
        currentLocation[constants.locationName] = list[0][constants.locationName];
        
        const currentDivision = {};
        currentDivision[constants.divisionId] = list[0][constants.divisions][0][constants.divisionId];
        currentDivision[constants.divisionName] = list[0][constants.divisions][0][constants.divisionName];

        setLocationList(list);
        setSelectedLocation(currentLocation);
        setSelectedDivision(currentDivision);
        setIsLoading(false);
      }
      catch (err) {
        console.error(err);
        setDialog({
          title: err.title ? err.title : constants.messages.errorMessage, 
          code: err.status ? err.status : "N/A", 
          message: err.message, 
          isError: true});
        setIsLoading(false);
      }
    }

    getSeason();
  }, [selectedSeason, leagueType]);


  return (
    <div className={styles.App}>
      <MessageDialog/>

      <Header
        toggleSidebar={toggleSidebar}
        setSelectedMenuItem={setSelectedMenuItem}/>

      <div className={styles.Content}>
        {isSidebarShowing && <div className={styles.Sidebar}>
          {locationList && <Sidebar 
            seasonList={seasonList} locationList={locationList}
            selectedSeason={selectedSeason} setSelectedSeason={setSelectedSeason}
            selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation}
            selectedDivision={selectedDivision} setSelectedDivision={setSelectedDivision}
            selectedMenuItem={selectedMenuItem} setSelectedMenuItem={setSelectedMenuItem}
          />}
        </div>}
        
        <div className={styles.Body}>
          <div className={styles.Main}>
            <Routes>
              <Route path="*" element={<NotFound />} />
              <Route 
                path="/" 
                element={<Home 
                          setSelectedMenuItem={setSelectedMenuItem}
                          currentWeekMatchups={currentWeekMatchups}
                          lastWeekMatchups={lastWeekMatchups}/>}/>
              <Route
                path="/player-registration"
                element={<Registration setSelectedMenuItem={setSelectedMenuItem} title={constants.titles.playerRegistration} form={constants.forms.player} />}/>
              <Route
                path="/club-registration"
                element={<Registration setSelectedMenuItem={setSelectedMenuItem} title={constants.titles.clubRegistration} form={constants.forms.club}/>}/>
              <Route
                path="/local-registration"
                element={<Registration setSelectedMenuItem={setSelectedMenuItem} title={constants.titles.localRegistration} form={constants.forms.local}/>}/>
              <Route 
                path="/leagues/*"
                element={selectedDivision[constants.divisionId] == "" ? <></> : <Leagues 
                          locationList={locationList}
                          selectedLocation={selectedLocation}
                          selectedDivision={selectedDivision}
                          setSelectedDivision={setSelectedDivision}
                          selectedMenuItem={selectedMenuItem}
                          setSelectedMenuItem={setSelectedMenuItem}/>}/>
              <Route 
                path="/local-leagues/*"
                element={selectedDivision[constants.divisionId] == "" ? <></> : <Leagues 
                          locationList={locationList}
                          selectedLocation={selectedLocation}
                          selectedDivision={selectedDivision}
                          setSelectedDivision={setSelectedDivision}
                          selectedMenuItem={selectedMenuItem}
                          setSelectedMenuItem={setSelectedMenuItem}/>}/>
              <Route 
                path="/gender-leagues/*"
                element={selectedDivision[constants.divisionId] == "" ? <></> : <Leagues 
                          locationList={locationList}
                          selectedLocation={selectedLocation}
                          selectedDivision={selectedDivision}
                          setSelectedDivision={setSelectedDivision}
                          selectedMenuItem={selectedMenuItem}
                          setSelectedMenuItem={setSelectedMenuItem}/>}/>
              <Route 
                path="/contact-us" 
                element={<ContactUs setSelectedMenuItem={setSelectedMenuItem}/>}/>
              <Route 
                path="/incident-report" 
                element={<IncidentReport setSelectedMenuItem={setSelectedMenuItem}/>}/>
              <Route 
                path="/about-us" 
                element={<AboutUs setSelectedMenuItem={setSelectedMenuItem}/>}/>
              <Route
                path="/privacy"
                element={<Privacy setSelectedMenuItem={setSelectedMenuItem}/>}/>
            </Routes>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
}
export default App;
